import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Card,
  CardContent,
  Grid,
  Alert,
  TextField,
  Fab,
  IconButton,
  Collapse
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

const CharacterInfoModal = ({ open, onClose, characterData, onAddCharacter }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCharacter, setNewCharacter] = useState({
    name: '',
    description: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCharacter(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddCharacter = async () => {

    // Call parent handler to add character
   await onAddCharacter(newCharacter);
    
    // Reset form
    setNewCharacter({ name: '', description: '' });
    setShowAddForm(false);
  };

  const toggleAddForm = () => {
    setShowAddForm(prev => !prev);
    if (showAddForm) {
      setNewCharacter({ name: '', description: '' });
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
            <PeopleIcon sx={{ mr: 1 }} /> Character Information
          </Typography>
          <Fab 
            color="primary" 
            aria-label="add" 
            size="small" 
            onClick={toggleAddForm}
            sx={{ boxShadow: 1 }}
          >
            {showAddForm ? <CloseIcon /> : <AddIcon />}
          </Fab>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Collapse in={showAddForm}>
          <Card variant="outlined" sx={{ mb: 3, p: 2, bgcolor: 'background.paper' }}>
            <Typography variant="h6" gutterBottom>
              Add New Character
            </Typography>
            <Box component="form" noValidate autoComplete="off">
              <TextField
                fullWidth
                required
                label="Character Name"
                name="name"
                value={newCharacter.name}
                onChange={handleInputChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={newCharacter.description}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={2}
                placeholder="Gender, age, relationship to other characters, and any other relevant information"
              />
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleAddCharacter}
                >
                  Save Character
                </Button>
              </Box>
            </Box>
          </Card>
        </Collapse>

        {!characterData ? (
          <Alert severity="info">
            Loading character information...
          </Alert>
        ) : characterData?.characters?.length > 0 ? (
          <Grid container spacing={3}>
            {characterData.characters.map((character, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon sx={{ mr: 1 }} />
                      {character.name || 'Unknown Character'}
                    </Typography>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    {character.description && (
                      <Box mt={2}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                          <DescriptionIcon fontSize="small" sx={{ mr: 0.5 }} /> Description
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {character.description}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="info">
            No character information available for this comic. You can add characters using the "+" button.
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CharacterInfoModal;
